import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideIconsModule } from '../../lucide-icons.module';
import { buildImageCandidates, ImageCandidateInput } from '../../utils/property-image.util';

/**
 * Reusable image element with a clean loading experience:
 *  - shows an animate-pulse skeleton until the real image has loaded,
 *  - fades the image in (opacity) once it loads,
 *  - on error, RETRIES the same URL a few times (handles transient backend
 *    failures like Catalyst concurrency-limit 429s), then advances through a
 *    fallback chain of candidate URLs,
 *  - when all candidates fail (or none exist), renders a projected fallback
 *    (`<ng-content>`) or a default building icon.
 *
 * Accepts either a single `src`, an explicit `candidates` list, or a
 * `primary` + `images` pair (the property shape) — the fallback chain is built
 * via `buildImageCandidates()`, the same logic the property card uses.
 */
@Component({
    selector: 'app-image-with-skeleton',
    standalone: true,
    imports: [CommonModule, LucideIconsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="relative w-full h-full overflow-hidden" [ngClass]="aspectClass">
            <ng-container *ngIf="currentSrc as src; else fallback">
                <div *ngIf="!loaded" class="absolute inset-0 animate-pulse bg-[color:var(--hover)]"></div>
                <img
                    [src]="src"
                    (load)="onLoad()"
                    (error)="onError()"
                    [alt]="alt"
                    class="absolute inset-0 block h-full w-full object-cover transition-opacity duration-300"
                    [class.opacity-0]="!loaded"
                    [class.opacity-100]="loaded"
                    [ngClass]="imgClass"
                />
            </ng-container>
            <ng-template #fallback>
                <div class="absolute inset-0 flex items-center justify-center bg-[color:var(--muted)]">
                    <ng-content select="[fallback]"></ng-content>
                    <lucide-icon
                        *ngIf="!hasProjectedFallback"
                        [name]="fallbackIcon"
                        class="h-16 w-16 text-[color:var(--hover-strong)]"
                    ></lucide-icon>
                </div>
            </ng-template>
        </div>
    `
})
export class ImageWithSkeletonComponent implements OnChanges, OnDestroy {
    /** A single image URL. */
    @Input() src: string | null = null;
    /** An explicit ordered fallback chain (takes precedence over `src`/`primary`). */
    @Input() candidates: string[] | null = null;
    /** Property-shaped inputs: a primary URL + image list, expanded via buildImageCandidates(). */
    @Input() primary: string | null = null;
    @Input() images: ImageCandidateInput[] | null = null;

    @Input() alt = 'Image';
    @Input() imgClass = '';
    /** Tailwind aspect class for the wrapper (e.g. 'aspect-video', 'aspect-square'). */
    @Input() aspectClass = 'aspect-video';
    @Input() fallbackIcon = 'building-2';
    /** Set true when projecting a custom [fallback] so the default icon is hidden. */
    @Input() hasProjectedFallback = false;

    private static readonly MAX_RETRIES = 2;
    private static readonly RETRY_DELAY_MS = 400;

    loaded = false;
    private chain: string[] = [];
    private index = 0;
    private retryCount = 0;
    private retryNonce = 0;
    private retryTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly cdr = inject(ChangeDetectorRef);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['src'] || changes['candidates'] || changes['primary'] || changes['images']) {
            this.rebuild();
        }
    }

    ngOnDestroy(): void {
        this.clearRetryTimer();
    }

    get currentSrc(): string | null {
        const base = this.chain[this.index] || null;
        if (!base) return null;
        // Append a retry nonce to force a fresh HTTP request on retry. The
        // /image/:fileId endpoint strips unknown query params (only w/h/fit/etc
        // are forwarded), so this doesn't change server behavior — it just
        // prevents the browser from reusing a cached failure response.
        if (this.retryNonce > 0) {
            const sep = base.includes('?') ? '&' : '?';
            return `${base}${sep}_retry=${this.retryNonce}`;
        }
        return base;
    }

    onLoad(): void {
        this.loaded = true;
        this.retryCount = 0;
        this.retryNonce = 0;
    }

    onError(): void {
        this.loaded = false;

        if (this.retryCount < ImageWithSkeletonComponent.MAX_RETRIES && this.chain[this.index]) {
            const attempt = this.retryCount;
            this.retryCount++;
            this.retryTimer = setTimeout(() => {
                this.retryNonce++;
                this.cdr.markForCheck();
            }, ImageWithSkeletonComponent.RETRY_DELAY_MS * (attempt + 1));
            return;
        }

        // Exhausted retries for this candidate — advance to the next one.
        this.retryCount = 0;
        this.retryNonce = 0;
        if (this.index + 1 < this.chain.length) {
            this.index += 1;
            return;
        }
        // Exhausted: clear so the fallback template renders.
        this.chain = [];
        this.index = 0;
    }

    private clearRetryTimer(): void {
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
    }

    private rebuild(): void {
        this.clearRetryTimer();
        this.loaded = false;
        this.index = 0;
        this.retryCount = 0;
        this.retryNonce = 0;
        if (this.candidates && this.candidates.length) {
            this.chain = Array.from(new Set(this.candidates.filter(Boolean) as string[]));
        } else {
            this.chain = buildImageCandidates(this.primary ?? this.src, this.images);
        }
    }
}
